import React, { useState } from 'react';
import { MapPin, X, Tag, Info } from 'lucide-react';
import { UF_OPTIONS } from '../constants/brazilianStates';

/**
 * Modal de cadastro de nova área.
 * Inclui campo de palavras-chave (chips) usadas pelo agent_detector_ofertas
 * para encontrar menções à área nos emails capturados.
 */
const CreateAreaModal = ({ onClose, onSave }) => {
  const [nome, setNome] = useState('');
  const [descricao, setDescricao] = useState('');
  const [cidade, setCidade] = useState(''); // inicia VAZIO — sem default São Paulo
  const [uf, setUf] = useState('');         // inicia VAZIO — sem default SP
  const [palavrasChave, setPalavrasChave] = useState([]);
  const [inputPalavra, setInputPalavra] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const adicionarPalavra = () => {
    const termo = inputPalavra.trim();
    if (!termo || termo.length < 2) return;
    if (palavrasChave.includes(termo)) {
      setInputPalavra('');
      return;
    }
    setPalavrasChave([...palavrasChave, termo]);
    setInputPalavra('');
  };

  const removerPalavra = (idx) => {
    setPalavrasChave(palavrasChave.filter((_, i) => i !== idx));
  };

  const canSave = !!nome.trim() && !!cidade.trim() && !!uf;

  const handleSave = async () => {
    if (!nome.trim()) {
      setError('Nome da área é obrigatório');
      return;
    }
    if (!cidade.trim() || !uf) {
      setError('Cidade e UF são obrigatórios');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      // Se digitou palavra mas não clicou Enter, adiciona antes de salvar
      const palavrasFinais = inputPalavra.trim()
        ? [...palavrasChave, inputPalavra.trim()]
        : palavrasChave;

      await onSave({
        nome: nome.trim(),
        descricao: descricao.trim() || null,
        localizacao_cidade: cidade.trim(),
        localizacao_uf: uf,
        palavras_chave: palavrasFinais,
      });
      onClose();
    } catch (err) {
      setError(err.message || 'Erro ao criar área');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-[10001] p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">

        <div className="p-5 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-lg font-semibold text-slate-800 flex items-center gap-2">
            <MapPin className="text-purple-600" size={20} />
            Nova Área
          </h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={22} />
          </button>
        </div>

        <div className="p-5 space-y-4">

          {error && (
            <div className="bg-red-50 border border-red-200 rounded p-2 text-sm text-red-700">
              {error}
            </div>
          )}

          {/* Nome */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Nome da área <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="Ex: 043.042 (3.038m²) Gama Lobo - Ipiranga"
              autoFocus
            />
          </div>

          {/* Descrição */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              Descrição <span className="text-slate-400 font-normal">(opcional)</span>
            </label>
            <textarea
              value={descricao}
              onChange={(e) => setDescricao(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm resize-none focus:outline-none focus:ring-2 focus:ring-purple-500"
              placeholder="Detalhes da área (zoneamento, viabilidade, etc.)"
            />
          </div>

          {/* Localização (agrupada) */}
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 space-y-3">
            <div className="text-xs font-bold uppercase tracking-wide text-slate-600 flex items-center gap-1.5">
              <MapPin size={13} className="text-purple-600" /> Localização
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div className="col-span-2">
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Cidade <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={cidade}
                  onChange={(e) => setCidade(e.target.value)}
                  className="w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                  placeholder="Ex: São Paulo"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  UF <span className="text-red-500">*</span>
                </label>
                <select
                  value={uf}
                  onChange={(e) => setUf(e.target.value)}
                  className="w-full px-2 py-2 border border-slate-300 rounded-lg text-sm bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-purple-500"
                >
                  <option value="">Selecione...</option>
                  {UF_OPTIONS.map((u) => <option key={u} value={u}>{u}</option>)}
                </select>
              </div>
            </div>
          </div>

          {/* Palavras-chave */}
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1">
              <Tag size={12} className="inline mr-1" />
              Palavras-chave para busca em emails
            </label>
            <div className="bg-blue-50 border border-blue-200 rounded-md p-2 mb-2 flex items-start gap-1.5 text-xs text-blue-800">
              <Info size={12} className="flex-shrink-0 mt-0.5" />
              <span>
                A IA usará essas palavras para encontrar menções desta área nos emails capturados.
                Inclua variações: nome, código, metragem, endereço, apelidos. Ex: <strong>Gama Lobo</strong>, <strong>043.042</strong>, <strong>3.038m²</strong>, <strong>Ipiranga</strong>.
              </span>
            </div>

            {/* Chips */}
            {palavrasChave.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {palavrasChave.map((p, i) => (
                  <span
                    key={i}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-purple-100 text-purple-800 text-xs"
                  >
                    {p}
                    <button
                      onClick={() => removerPalavra(i)}
                      className="hover:bg-purple-200 rounded-full p-0.5"
                      title="Remover"
                    >
                      <X size={10} />
                    </button>
                  </span>
                ))}
              </div>
            )}

            {/* Input */}
            <div className="flex gap-2">
              <input
                type="text"
                value={inputPalavra}
                onChange={(e) => setInputPalavra(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ',') {
                    e.preventDefault();
                    adicionarPalavra();
                  }
                }}
                onBlur={() => {
                  if (inputPalavra.trim()) adicionarPalavra();
                }}
                className="flex-grow px-3 py-1.5 border border-slate-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
                placeholder="Digite e pressione Enter ou vírgula"
              />
              <button
                onClick={adicionarPalavra}
                disabled={!inputPalavra.trim() || inputPalavra.trim().length < 2}
                className="px-3 py-1.5 bg-purple-100 text-purple-700 rounded-lg text-sm hover:bg-purple-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Adicionar
              </button>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-slate-200 bg-slate-50 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-200 rounded-lg"
          >
            Cancelar
          </button>
          <button
            onClick={handleSave}
            disabled={!canSave || saving}
            className="px-4 py-2 text-sm bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5"
          >
            <MapPin size={14} />
            {saving ? 'Salvando...' : 'Criar Área'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateAreaModal;
